import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { pick3 } from "../i18n/v3";
import { grantAnalyticsConsent, denyAnalyticsConsent, getConsentState, getConsentPreferences } from "../hooks/useAnalytics";

/**
 * ConsentBanner — first-visit cookie preferences.
 *
 * Three equally-weighted actions (accept-all, reject-non-essential,
 * manage preferences) so rejection is never harder than acceptance.
 * The banner can be reopened at any time by calling
 * `window.__liap.openConsent()` — the Cookie policy uses this to
 * expose a "Cookie preferences" link.
 */

const COPY = {
    heading: {
        en: "Cookie preferences", es: "Preferencias de cookies",
        nl: "Cookievoorkeuren",   fr: "Préférences de cookies",
    },
    body: {
        en: "This site uses one privacy-respecting analytics service (Google Analytics 4) to measure how essays are read. No advertising cookies, no user tracking. Nothing loads until you decide.",
        es: "Este sitio utiliza un único servicio de analítica respetuoso con la privacidad (Google Analytics 4) para saber cómo se leen los ensayos. Sin cookies publicitarias, sin seguimiento. Nada se carga hasta que decidas.",
        nl: "Deze site gebruikt één privacyvriendelijke analysedienst (Google Analytics 4) om te meten hoe essays worden gelezen. Geen advertentiecookies, geen tracking. Er wordt niets geladen totdat je beslist.",
        fr: "Ce site utilise un seul service d’analyse respectueux de la vie privée (Google Analytics 4) pour mesurer la lecture des essais. Aucun cookie publicitaire, aucun pistage. Rien ne se charge tant que vous n’avez pas décidé.",
    },
    detailsIntro: {
        en: "Choose which categories may be used:",
        es: "Elige qué categorías pueden utilizarse:",
        nl: "Kies welke categorieën gebruikt mogen worden:",
        fr: "Choisissez les catégories qui peuvent être utilisées :",
    },
    catNecessary: {
        title:  { en: "Strictly necessary", es: "Estrictamente necesarias", nl: "Strikt noodzakelijk", fr: "Strictement nécessaires" },
        desc:   { en: "Remember your language and this consent choice. Always active.", es: "Recuerda tu idioma y esta elección de consentimiento. Siempre activas.", nl: "Onthoudt uw taal en deze keuze. Altijd actief.", fr: "Retient votre langue et ce choix de consentement. Toujours actif." },
    },
    catAnalytics: {
        title:  { en: "Analytics", es: "Analítica", nl: "Analyse", fr: "Analytique" },
        desc:   { en: "Google Analytics 4 with anonymised IP. Optional.", es: "Google Analytics 4 con IP anonimizada. Opcional.", nl: "Google Analytics 4 met geanonimiseerd IP. Optioneel.", fr: "Google Analytics 4 avec IP anonymisée. Optionnel." },
    },
    acceptAll:   { en: "Accept all",             es: "Aceptar todas",             nl: "Alles accepteren",       fr: "Tout accepter" },
    rejectOpt:   { en: "Reject non-essential",   es: "Rechazar no esenciales",    nl: "Weiger niet-essentieel", fr: "Refuser les non-essentiels" },
    manage:      { en: "Manage preferences",     es: "Gestionar preferencias",    nl: "Voorkeuren beheren",     fr: "Gérer les préférences" },
    save:        { en: "Save preferences",       es: "Guardar preferencias",      nl: "Voorkeuren opslaan",     fr: "Enregistrer les préférences" },
    cookiePolicy:{ en: "Cookie policy",          es: "Política de cookies",       nl: "Cookiebeleid",           fr: "Politique des cookies" },
};

export default function ConsentBanner() {
    const { lang } = useLanguage();
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [analyticsOn, setAnalyticsOn] = useState(false);

    useEffect(() => {
        if (getConsentState() === null) setVisible(true);
        // Global reopener API for the Cookie policy link
        window.__liap = window.__liap || {};
        window.__liap.openConsent = () => {
            // Reopen with the visitor's actual stored choices — never a generic state.
            const prefs = getConsentPreferences();
            setAnalyticsOn(prefs ? prefs.analytics : false);
            setExpanded(true);
            setVisible(true);
        };
    }, []);

    if (!visible) return null;

    const finish = (granted) => {
        granted ? grantAnalyticsConsent() : denyAnalyticsConsent();
        setVisible(false);
        setExpanded(false);
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    role="dialog"
                    aria-live="polite"
                    aria-label={pick3(COPY.heading, lang)}
                    data-testid="consent-banner"
                    initial={{ y: 60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 60, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="fixed z-50 bottom-4 left-4 right-4 lg:left-auto lg:right-6 lg:bottom-6 lg:max-w-[480px] bg-[color:var(--ivory)] border border-[color:var(--line-strong)] shadow-[0_18px_40px_-20px_rgba(16,42,67,0.35)]"
                >
                    <div className="p-5 md:p-6">
                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-3 flex items-center gap-3">
                            <span aria-hidden className="inline-block w-6 h-px bg-[color:var(--gold)]" />
                            <span>{pick3(COPY.heading, lang)}</span>
                        </div>
                        <p className="text-[14px] leading-[1.6] text-[color:var(--navy)]/90 mb-5 max-w-[46ch]">
                            {pick3(COPY.body, lang)}
                        </p>

                        {expanded ? (
                            <div data-testid="consent-details" className="mb-5">
                                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-3">
                                    {pick3(COPY.detailsIntro, lang)}
                                </p>
                                <ul className="space-y-3 text-[13px] leading-[1.55] text-[color:var(--navy)]/85">
                                    <li className="flex items-start gap-3">
                                        <input type="checkbox" checked disabled className="mt-1 accent-[color:var(--navy)]" aria-label={pick3(COPY.catNecessary.title, lang)} />
                                        <div>
                                            <strong className="font-display italic text-[15px] text-[color:var(--navy)]">{pick3(COPY.catNecessary.title, lang)}</strong>
                                            <p className="mt-1">{pick3(COPY.catNecessary.desc, lang)}</p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <input
                                            type="checkbox"
                                            checked={analyticsOn}
                                            data-testid="consent-toggle-analytics"
                                            onChange={(e) => setAnalyticsOn(e.target.checked)}
                                            className="mt-1 accent-[color:var(--navy)]"
                                            aria-label={pick3(COPY.catAnalytics.title, lang)}
                                        />
                                        <div>
                                            <strong className="font-display italic text-[15px] text-[color:var(--navy)]">{pick3(COPY.catAnalytics.title, lang)}</strong>
                                            <p className="mt-1">{pick3(COPY.catAnalytics.desc, lang)}</p>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        ) : null}

                        <div className="flex flex-wrap items-center gap-3">
                            {expanded ? (
                                <button type="button" data-testid="consent-save" onClick={() => finish(analyticsOn)} className="btn-editorial">
                                    {pick3(COPY.save, lang)}
                                    <span aria-hidden className="w-4 h-px bg-current opacity-70" />
                                </button>
                            ) : (
                                <>
                                    <button type="button" data-testid="consent-accept-all" onClick={() => finish(true)} className="btn-editorial">
                                        {pick3(COPY.acceptAll, lang)}
                                        <span aria-hidden className="w-4 h-px bg-current opacity-70" />
                                    </button>
                                    <button type="button" data-testid="consent-reject" onClick={() => finish(false)} className="btn-editorial btn-ghost">
                                        {pick3(COPY.rejectOpt, lang)}
                                    </button>
                                    <button type="button" data-testid="consent-manage" onClick={() => setExpanded(true)} className="link-editorial font-display italic text-[15px] text-[color:var(--navy)]/85 ml-1">
                                        {pick3(COPY.manage, lang)}
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="mt-4">
                            <Link
                                to="/cookies"
                                data-testid="consent-cookies-link"
                                onClick={() => setVisible(false)}
                                className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                            >
                                {pick3(COPY.cookiePolicy, lang)}
                            </Link>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
